import { z } from 'zod';
import {
  ChipExportBundleSchema,
  type ChipExportBundle,
  type ChipModelDto,
  assertProjectDefinitionReferences,
} from '@/schemas/chip';
import { CircuitType, ChipIdSchema } from '@/schemas/common';
import type { IChipModel } from '@/interfaces/models';
import { ProjectSnapshotBuilder, type ProjectSnapshot } from './ProjectSnapshot';
import { nodeSnapshotWallAt, withNodeSnapshotTime } from '@/models/logic/SavedNodeState';
import {
  decodeCurrentProjectDto,
  encodeProjectSnapshot,
  encodeCurrentProjectDto,
} from './CurrentProjectFormat';

export const CURRENT_CHIP_EXTENSION = '.chip.digitiles';
export const CURRENT_CHIP_MIME = 'application/vnd.digitiles.compact-chip';
const MAGIC = 'DGTLCHP1',
  HEADER_BYTES = 16,
  MAX_METADATA_BYTES = 64 * 1024;
const Envelope = z
  .object({ version: z.literal(1), rootChipId: ChipIdSchema, exportedAt: z.string() })
  .strict();

/** Validate a complete file before any model factory or active-store mutation. */
export function validateChipFileBundle(input: unknown): ChipExportBundle {
  const bundle = ChipExportBundleSchema.parse(input);
  const chips = new Map(bundle.chips.map((chip) => [chip.id, chip]));
  if (chips.size !== bundle.chips.length)
    throw new Error('Duplicate chip IDs in chip file.');
  if (!chips.has(bundle.rootChipId)) throw new Error('The root chip is missing.');
  assertProjectDefinitionReferences(
    { mainChipId: bundle.rootChipId, chips: bundle.chips },
    true,
  );
  const instanceIds = new Set<string>();
  for (const chip of bundle.chips) {
    if (chip.editingInstanceDto.chipId !== chip.id)
      throw new Error('Invalid editing instance owner.');
    const placed = new Set(chip.chipInstanceDtos.map((instance) => instance.id));
    for (const instance of [...chip.chipInstanceDtos, chip.editingInstanceDto]) {
      if (instanceIds.has(instance.id)) throw new Error('Duplicate chip instance IDs.');
      instanceIds.add(instance.id);
      if (!chips.has(instance.chipId)) throw new Error('A chip dependency is missing.');
      const nodes = new Set(instance.logicGraphDto.nodes.map((node) => node.id));
      const edges = new Set(instance.logicGraphDto.edges.map((edge) => edge.id));
      if (
        nodes.size !== instance.logicGraphDto.nodes.length ||
        edges.size !== instance.logicGraphDto.edges.length
      )
        throw new Error('Duplicate runtime identities.');
      // The project codec preserves non-local edge spellings and owner-factored
      // graphs. Runtime restoration, not the file envelope, resolves references.
    }
    const links = new Set(chip.interfaceLinks.map((link) => link.id));
    if (links.size !== chip.interfaceLinks.length)
      throw new Error('Duplicate interface link IDs.');
    const circuits = [chip.logicCircuitDto, chip.structureCircuitDto];
    const tileSets = circuits.map((circuit, index) => {
      if (
        circuit.circuitType !==
        (index ? CircuitType.ChipStructure : CircuitType.ChipLogic)
      )
        throw new Error('Invalid chip circuit type.');
      const ids = new Set(circuit.tileManagerDto.tiles.map((tile) => tile.id));
      if (ids.size !== circuit.tileManagerDto.tiles.length)
        throw new Error('Duplicate tile IDs.');
      const positions = new Set<string>();
      for (const tile of circuit.tileManagerDto.tiles) {
        const { location, circuitType, chipInstanceId } = tile.globalTileLocation;
        if (
          circuitType !== circuit.circuitType ||
          chipInstanceId !== chip.editingInstanceDto.id ||
          !Number.isInteger(location.x) ||
          !Number.isInteger(location.y) ||
          location.x < -16383 ||
          location.x > 16384 ||
          location.y < -16383 ||
          location.y > 16384
        )
          throw new Error('Invalid chip tile location.');
        const key = `${location.x},${location.y}`;
        if (positions.has(key)) throw new Error('Multiple tiles occupy one location.');
        positions.add(key);
        if (tile.meta.partOfChip && (index !== 0 || !placed.has(tile.meta.partOfChip)))
          throw new Error('A tile has a missing chip instance.');
      }
      for (const edge of [
        ...(circuit.connectionGraphDto?.connections ?? []),
        ...(circuit.connectionGraphDto?.disconnected ?? []),
      ])
        if (!ids.has(edge.a.tileId) || !ids.has(edge.b.tileId))
          throw new Error('A connection refers to a missing tile.');
      return ids;
    });
    for (const link of chip.interfaceLinks)
      if (
        (link.logicTileId && !tileSets[0].has(link.logicTileId)) ||
        (link.structureTileId && !tileSets[1].has(link.structureTileId))
      )
        throw new Error('An interface link refers to a missing tile.');
  }
  const seen = new Set<string>(),
    pending: string[] = [bundle.rootChipId];
  for (let i = 0; i < pending.length; i++) {
    const id = pending[i];
    if (seen.has(id)) continue;
    seen.add(id);
    for (const instance of chips.get(id as typeof bundle.rootChipId)!.chipInstanceDtos)
      pending.push(instance.chipId);
  }
  if (seen.size !== chips.size)
    throw new Error('Chip file contains unrelated definitions.');
  return bundle;
}

/** The existing dependency-first ordering, with an iterative cycle-safe walk. */
export function orderChipFileDefinitions(chips: ChipModelDto[]): ChipModelDto[] {
  const byId = new Map(chips.map((chip) => [chip.id, chip])),
    seen = new Set<string>(),
    result: ChipModelDto[] = [];
  for (const chip of chips) {
    const stack = [{ chip, finish: false }];
    while (stack.length) {
      const current = stack.pop()!;
      if (current.finish) {
        result.push(current.chip);
        continue;
      }
      if (seen.has(current.chip.id)) continue;
      seen.add(current.chip.id);
      stack.push({ chip: current.chip, finish: true });
      for (let i = current.chip.chipInstanceDtos.length - 1; i >= 0; i--) {
        const dependency = byId.get(current.chip.chipInstanceDtos[i].chipId);
        if (!dependency) throw new Error('A chip dependency is missing.');
        stack.push({ chip: dependency, finish: false });
      }
    }
  }
  return result;
}

async function wrap(
  snapshot: Promise<Blob>,
  rootChipId: string,
  exportedAt: string,
): Promise<Blob> {
  const metadata = new TextEncoder().encode(
    JSON.stringify(Envelope.parse({ version: 1, rootChipId, exportedAt })),
  );
  if (metadata.byteLength > MAX_METADATA_BYTES)
    throw new Error('Chip file metadata is too large.');
  const header = new ArrayBuffer(HEADER_BYTES);
  new Uint8Array(header).set(new TextEncoder().encode(MAGIC));
  new DataView(header).setUint32(8, metadata.byteLength, true);
  return new Blob([header, metadata, await snapshot], { type: CURRENT_CHIP_MIME });
}

/** Native synchronous capture of only the root and its dependency closure. */
export function encodeCurrentChip(
  chip: IChipModel,
  exportedAt = new Date().toISOString(),
): Promise<Blob> {
  const builder = new ProjectSnapshotBuilder();
  try {
    const chips: IChipModel[] = [],
      seen = new Set<string>([chip.id]),
      pending = [chip];
    for (let i = 0; i < pending.length; i++) {
      const current = pending[i];
      chips.push(current);
      if (chips.length > 100_000)
        throw new Error('Chip dependency closure exceeds limit.');
      for (const instance of current.logicCircuit.chipInstances.values()) {
        const dependency = instance.chip;
        if (!dependency) throw new Error('A chip dependency is missing.');
        if (!seen.has(dependency.id)) {
          seen.add(dependency.id);
          pending.push(dependency);
          if (pending.length > 100_000)
            throw new Error('Chip dependency closure exceeds limit.');
        }
      }
    }
    const snapshots = withNodeSnapshotTime(nodeSnapshotWallAt(), () =>
      chips.map((current) => {
        if (!current.captureSnapshot)
          throw new Error('This chip does not support compact capture.');
        return current.captureSnapshot(builder);
      }),
    );
    const snapshot: ProjectSnapshot = builder.finish({
      name: chip.name,
      mainChipId: chip.id,
      chips: snapshots,
    }).project;
    return wrap(encodeProjectSnapshot(snapshot), chip.id, exportedAt);
  } catch (error) {
    builder.abandon();
    return Promise.reject(error);
  }
}

/** Converter/object boundary only; ordinary chip-file import never parses legacy JSON. */
export function encodeCurrentChipBundle(input: unknown): Promise<Blob> {
  try {
    const bundle = validateChipFileBundle(input),
      root = bundle.chips.find((chip) => chip.id === bundle.rootChipId)!;
    return wrap(
      encodeCurrentProjectDto({
        name: root.name,
        mainChipId: root.id,
        chips: bundle.chips,
      }),
      bundle.rootChipId,
      bundle.exportedAt,
    );
  } catch (error) {
    return Promise.reject(error);
  }
}

export async function inspectChipFormat(
  blob: Blob,
): Promise<'current' | 'legacy-json' | 'unsupported'> {
  const prefix = await blob.slice(0, 256).text();
  return prefix.startsWith(MAGIC)
    ? 'current'
    : /^\s*\{/.test(prefix)
      ? 'legacy-json'
      : 'unsupported';
}

export async function decodeCurrentChipBundle(blob: Blob): Promise<ChipExportBundle> {
  if (blob.size < HEADER_BYTES || blob.size > 193 * 1024 * 1024)
    throw new Error('Invalid chip file size.');
  const header = await blob.slice(0, HEADER_BYTES).arrayBuffer(),
    view = new DataView(header);
  if (new TextDecoder().decode(header.slice(0, 8)) !== MAGIC || view.getUint32(12, true))
    throw new Error('Unsupported chip file.');
  const length = view.getUint32(8, true);
  if (length > MAX_METADATA_BYTES || HEADER_BYTES + length >= blob.size)
    throw new Error('Invalid chip file metadata.');
  const envelope = Envelope.parse(
    JSON.parse(await blob.slice(HEADER_BYTES, HEADER_BYTES + length).text()),
  );
  const project = await decodeCurrentProjectDto(
    blob.slice(HEADER_BYTES + length),
    undefined,
    undefined,
    undefined,
    { allowTemplateRoot: true },
  );
  if (project.mainChipId !== envelope.rootChipId)
    throw new Error('Chip file root does not match its payload.');
  return validateChipFileBundle({ ...envelope, chips: project.chips });
}
